/**
 * ReactionChips — the reactions on a bubble (PR-4, 2026-09-09; restyled
 * 2026-09-09): one chip per emoji with its count; the viewer's own reactions
 * read as pressed; a click toggles; the accessible label names who reacted.
 * Lives at the head of the meta line (MetaLeading — Miles: "be a part of the
 * bottom line of a message"), never in its own row.
 *
 * CONTRAST (Miles, 2026-09-09, screenshot of a gold bubble with a faint
 * translucent circle and a ~10 px emoji: "need more contrast and need to be
 * colorful like they are but somehow need to be seen better"): a solid,
 * near-white pill with a hairline border and a soft shadow reads on the gold
 * user bubble AND on the soft-white Prest0n bubble; the emoji stays the
 * platform's own colourful glyph at a size you can actually see; the count is
 * bold; the viewer's own chip carries a gold outline. Alpha tints and #fff
 * pass the design-token gate.
 *
 * WHO REACTED (2026-09-19, Miles: on mobile "there's no way" to see it). The
 * names lived in `title` alone, which a touch browser never renders — so the
 * answer existed on a desktop after a second of hover and nowhere else. Two
 * doors now, because the two obvious gestures were already spoken for (a tap
 * toggles, a long press on the bubble opens the picker):
 *   • the trailing › pill, ALWAYS VISIBLE — nothing here depends on knowing a
 *     hidden gesture, and it sits inside this group rather than as a second
 *     row or a second cluster of initials beside the seen-by marks, so the one
 *     bottom line stays one line;
 *   • a press-and-hold on a chip, routed by the host ahead of the picker.
 * On a fine pointer, hovering or focusing a chip opens the styled hover card
 * instead of the OS tooltip — instant, and truncated by the model rather than
 * by the window manager. `title` is gone: it would fire underneath the card.
 */
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import type { ReactionSummaryEntry } from '../../services/messageReactions/reactionModel';
import type { PickerAnchor, PickerRect } from '../../services/messageReactions/pickerPlacement';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';
import { ReactionHoverCard } from './ReactionDetails';

export interface ReactionChipsProps {
  reactions: ReadonlyArray<ReactionSummaryEntry>;
  /** uid → display name for the accessible label and the hover card. */
  labelFor: (uid: string) => string;
  onToggle: (emoji: string) => void;
  /** False while the message cannot take a reaction yet (not delivered). */
  canReact?: boolean;
  /** Opens the activity surface, anchored on the element pressed. Absent ⇒ no › pill and no hover card. */
  onShowDetails?: (anchorEl: HTMLElement) => void;
  /** One line of names for the hover card, from the shared model. */
  previewFor?: (entry: ReactionSummaryEntry) => string;
  /** The pane rect the hover card must stay inside, read at hover time. */
  boundsFor?: () => PickerRect | null;
}

export const ReactionChipsGroup = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  user-select: none;
`;

export const ReactionChip = styled.button<{ $mine?: boolean; $canReact?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px 2px 7px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$mine ? 'var(--brand-gold-text)' : 'rgba(0, 0, 0, 0.16)')};
  box-shadow: ${(p) => (p.$mine
    ? '0 0 0 1px var(--brand-gold-text), 0 1px 3px rgba(0, 0, 0, 0.2)'
    : '0 1px 3px rgba(0, 0, 0, 0.2)')};
  background: rgba(255, 255, 255, 0.94);
  color: var(--brand-black);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.8rem;
  line-height: 1.25;
  cursor: ${(p) => (p.$canReact === false ? 'default' : 'pointer')};
  appearance: none;
  -webkit-appearance: none;
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.24);
  }
  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 1px;
  }
  &:disabled {
    opacity: 0.9;
  }
`;

/**
 * The › pill: the always-visible door to "who reacted". Its geometry is the
 * chip's own, so it is exactly as tappable as the siblings it sits beside —
 * and it is never the toggle, whichever pill your thumb lands on.
 */
export const ReactionWhoButton = styled(ReactionChip)`
  padding: 2px 6px;
  gap: 0;
  color: var(--brand-text-secondary);

  svg {
    width: 13px;
    height: 13px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

/** The emoji at a size you can see: the platform's own colourful glyph, never an icon font. */
const Emoji = styled.span`
  font-size: 1.05rem;
  line-height: 1;
`;

const Count = styled.span`
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

export function reactionChipLabel(entry: ReactionSummaryEntry, labelFor: (uid: string) => string, lang: 'en' | 'es'): string {
  const names = entry.uids.map(labelFor).filter(Boolean);
  const who = names.join(', ');
  return `${entry.emoji} ${entry.count} — ${who}`;
}

const anchorOf = (el: HTMLElement): PickerAnchor => {
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, width: r.width, height: r.height };
};

export function ReactionChips({
  reactions, labelFor, onToggle, canReact = true, onShowDetails, previewFor, boundsFor,
}: ReactionChipsProps): React.ReactElement | null {
  const [hover, setHover] = useState<{ emoji: string; names: string; anchor: PickerAnchor; bounds: PickerRect | null } | null>(null);

  const openHover = useCallback((entry: ReactionSummaryEntry, el: HTMLElement | null) => {
    if (!el || !previewFor) return;
    const names = previewFor(entry);
    if (!names) return;
    setHover({ emoji: entry.emoji, names, anchor: anchorOf(el), bounds: boundsFor?.() ?? null });
  }, [previewFor, boundsFor]);
  const closeHover = useCallback(() => setHover(null), []);

  if (!reactions || reactions.length === 0) return null;
  const lang = getUserUiLanguage() === 'es' ? 'es' : 'en';
  return (
    <ReactionChipsGroup
      data-reaction-chips=""
      role="group"
      aria-label={lang === 'es' ? 'reacciones' : 'reactions'}
    >
      {reactions.map((entry) => {
        const label = reactionChipLabel(entry, labelFor, lang);
        return (
          <ReactionChip
            key={entry.emoji}
            type="button"
            data-reaction-chip={entry.emoji}
            data-reaction-count={entry.count}
            data-reaction-mine={entry.mine ? 'true' : undefined}
            aria-pressed={entry.mine}
            aria-label={label}
            disabled={!canReact}
            $mine={entry.mine}
            $canReact={canReact}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(entry.emoji);
            }}
            // A mouse only: a touch raises pointerenter too, and there the
            // sheet — not a card the finger is already covering — is the answer.
            onPointerEnter={(e) => { if (e.pointerType === 'mouse') openHover(entry, e.currentTarget as HTMLElement); }}
            onPointerLeave={closeHover}
            onFocus={(e) => openHover(entry, e.currentTarget as HTMLElement)}
            onBlur={closeHover}
          >
            <Emoji aria-hidden="true">{entry.emoji}</Emoji>
            <Count aria-hidden="true">{entry.count}</Count>
          </ReactionChip>
        );
      })}
      {onShowDetails && (
        <ReactionWhoButton
          type="button"
          data-reaction-who=""
          aria-label={lang === 'es' ? 'quién reaccionó' : 'who reacted'}
          onClick={(e) => {
            e.stopPropagation();
            closeHover();
            onShowDetails(e.currentTarget as HTMLElement);
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </ReactionWhoButton>
      )}
      {hover && (
        <ReactionHoverCard anchor={hover.anchor} bounds={hover.bounds} emoji={hover.emoji} names={hover.names} />
      )}
    </ReactionChipsGroup>
  );
}

export default ReactionChips;
